<script setup lang="ts">
import { Button } from '@dashhudson/dashing-ui/button';
import { Menu } from '@dashhudson/dashing-ui/menu';
import { SidePanelHeader, useSidePanel } from '@dashhudson/dashing-ui/side-panel';
import { VisionAiChip } from '@dashhudson/dashing-ui/vision-ai-chip';
import { computed } from 'vue';

import { useClearChatHistoryConfirmation } from '@/components/VisionAi/composables/useClearChatHistoryConfirmation';
import { useNewChatDisabled } from '@/components/VisionAi/composables/useNewChatDisabled';
import { VISION_PANEL_WIDTH_EXPANDED } from '@/components/VisionAi/constants';
import { useVisionAiStore } from '@/stores/vision-ai';

const props = defineProps<{
  isExpanded: boolean;
}>();

const emit = defineEmits<{
  'update:isExpanded': [value: boolean];
}>();

const { close, setWidth } = useSidePanel();
const visionAiStore = useVisionAiStore();
const { showClearChatHistoryConfirmation } = useClearChatHistoryConfirmation();
const { newChatDisabled, newChatTooltip } = useNewChatDisabled();

function handleClose() {
  close();
}

function handleToggleExpanded() {
  const expanded = !props.isExpanded;
  emit('update:isExpanded', expanded);
  setWidth(expanded ? VISION_PANEL_WIDTH_EXPANDED : null);
}

const menuOptions = computed(() => [
  {
    label: 'New Chat',
    icon: 'message-chat',
    disabled: newChatDisabled.value,
    tooltip: newChatTooltip.value,
    command: () => visionAiStore.startNewChat(),
  },
  {
    label: 'Help',
    icon: 'help-circle',
    command: () => visionAiStore.sendToResourceCenter(),
  },
  {
    label: 'Delete Entire Chat History',
    icon: 'slash-circle',
    class: 'text-danger fill-icon-danger',
    command: () => {
      showClearChatHistoryConfirmation();
    },
  },
]);
</script>

<template>
  <div class="px-8 pb-6 pt-7">
    <SidePanelHeader @close="handleClose">
      <template #title>
        <VisionAiChip label="Vision AI" />
      </template>
      <template #actions>
        <Menu :model="menuOptions" :dropdown="{ placement: 'bottom-end' }">
          <Button icon="dots-vertical" size="sm" severity="utility" aria-label="More options" />
        </Menu>
        <Button
          :icon="props.isExpanded ? 'collapse' : 'expand'"
          size="sm"
          severity="utility"
          :aria-label="props.isExpanded ? 'Collapse panel' : 'Expand panel'"
          @click="handleToggleExpanded"
        />
      </template>
    </SidePanelHeader>
  </div>
</template>
